import * as React from 'react';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Login as RaLogin, LoginForm } from 'react-admin';
import InsightsIcon from '@mui/icons-material/Insights';
import GroupsIcon from '@mui/icons-material/Groups';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import { appBarColors } from './appBarTheme';

/**
 * The sign-in screen.
 *
 * Two panels: what this is on the left, the form on the right. The left panel
 * collapses below the `md` breakpoint, where a split screen would leave the
 * form in a column too narrow to use -- a school administrator signing in from
 * a phone gets the form alone, full width.
 *
 * Built on react-admin's own Login rather than replacing it, so the pieces that
 * are easy to get wrong -- submitting, the error notification, redirecting to
 * wherever the session was interrupted -- keep working. What changes here is
 * the shell around <LoginForm />.
 *
 * Colours come from appBarColors, the same derivation the top bar uses, so the
 * sign-in screen and the panel behind it are recognisably the same product
 * under any of the six themes.
 */

/** A school, drawn rather than photographed -- no asset to ship or load. */
const SchoolIllustration = ({ tint }: { tint: string }) => (
    <Box
        component="svg"
        viewBox="0 0 240 150"
        role="presentation"
        sx={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block' }}
    >
        {/* ground */}
        <line x1="10" y1="132" x2="230" y2="132" stroke={alpha(tint, 0.35)} strokeWidth="1.5" />
        {/* flag */}
        <line x1="120" y1="14" x2="120" y2="34" stroke={alpha(tint, 0.7)} strokeWidth="2" />
        <polygon points="120,15 141,21 120,27" fill={alpha(tint, 0.55)} />
        {/* roof */}
        <polygon points="120,34 200,66 40,66" fill={alpha(tint, 0.3)} />
        <polygon points="120,34 200,66 40,66" fill="none" stroke={alpha(tint, 0.75)} strokeWidth="2" />
        {/* body */}
        <rect x="52" y="66" width="136" height="52" fill={alpha(tint, 0.16)} stroke={alpha(tint, 0.6)} strokeWidth="1.5" />
        {/* columns */}
        {[66, 88, 132, 154].map(x => (
            <rect key={x} x={x} y="74" width="9" height="44" fill={alpha(tint, 0.28)} />
        ))}
        {/* doorway */}
        <path
            d="M104 118 v-24 a16 16 0 0 1 32 0 v24 z"
            fill={alpha(tint, 0.4)}
            stroke={alpha(tint, 0.7)}
            strokeWidth="1.5"
        />
        {/* steps */}
        <rect x="46" y="118" width="148" height="6" fill={alpha(tint, 0.3)} />
        <rect x="38" y="124" width="164" height="8" fill={alpha(tint, 0.22)} />
        {/* a chart rising behind the roofline -- this is a measurement product */}
        <polyline
            points="196,52 206,44 216,48 228,28"
            fill="none"
            stroke={alpha(tint, 0.7)}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <circle cx="228" cy="28" r="3.5" fill={tint} />
    </Box>
);

const points = [
    { icon: <GroupsIcon fontSize="small" />, text: 'Every class, section and roster in one place' },
    { icon: <InsightsIcon fontSize="small" />, text: 'Progress measured from real attempts, not estimates' },
    { icon: <ShieldOutlinedIcon fontSize="small" />, text: 'Each school’s data kept in its own database' },
];

const BrandPanel = () => {
    const theme = useTheme();
    const { from, mid, to, fg } = appBarColors(theme);

    return (
        <Box
            sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 3,
                flex: '1 1 46%',
                p: 5,
                color: fg,
                backgroundColor: mid,
                backgroundImage: `linear-gradient(150deg, ${from} 0%, ${mid} 58%, ${alpha(to, 0.9)} 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at 20% 0%, ${alpha(fg, 0.18)}, transparent 55%)`,
                    pointerEvents: 'none',
                },
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 800,
                            bgcolor: alpha(fg, 0.18),
                            border: `1px solid ${alpha(fg, 0.35)}`,
                        }}
                    >
                        P
                    </Box>
                    <Box sx={{ lineHeight: 1.1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 17, letterSpacing: '.04em' }}>
                            Pratham
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                letterSpacing: '.14em',
                                textTransform: 'uppercase',
                                color: alpha(fg, 0.72),
                            }}
                        >
                            Admin Console
                        </Typography>
                    </Box>
                </Stack>

                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                    Run your school on
                    <br />
                    what the work shows.
                </Typography>
                <Typography variant="body2" sx={{ color: alpha(fg, 0.78), maxWidth: 380 }}>
                    Rosters, assignments, tests and class performance — for
                    teachers, heads of school, and the people supporting them.
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1, my: 1 }}>
                <SchoolIllustration tint={fg} />
            </Box>

            <Stack spacing={1.25} sx={{ position: 'relative', zIndex: 1 }}>
                {points.map(p => (
                    <Stack key={p.text} direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                flexShrink: 0,
                                borderRadius: 1.5,
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: alpha(fg, 0.14),
                            }}
                        >
                            {p.icon}
                        </Box>
                        <Typography variant="body2" sx={{ color: alpha(fg, 0.85) }}>
                            {p.text}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
};

const FormPanel = () => (
    <Box
        sx={{
            flex: '1 1 54%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 3, sm: 5 },
            py: { xs: 4, md: 6 },
            bgcolor: 'background.paper',
        }}
    >
        <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use the account your school administrator set up for you.
            </Typography>

            <Box
                sx={{
                    // LoginForm brings its own padding and a full-width button;
                    // only the outer spacing needs correcting.
                    '& form': { padding: 0 },
                    '& .MuiCardContent-root': { padding: 0 },
                    '& .MuiButton-root': { py: 1.1, fontWeight: 700 },
                }}
            >
                <LoginForm />
            </Box>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2.5 }}
            >
                Forgotten your password? Your school administrator can reset it.
            </Typography>
        </Box>
    </Box>
);

const LoginNew = () => (
    <RaLogin
        sx={theme => {
            const { from, mid } = appBarColors(theme);
            return {
                justifyContent: 'center',
                // The root is a full-height flex column; without this the card
                // touches the window edge on a narrow screen.
                padding: theme.spacing(2),
                backgroundImage: `linear-gradient(160deg, ${alpha(mid, 0.12)}, ${alpha(
                    from,
                    0.22
                )})`,
                backgroundColor: theme.palette.background.default,

                // react-admin puts a lock avatar above the children. This screen
                // says what it is in the panel beside the form, so the avatar is
                // one icon too many.
                '& .RaLogin-avatar': { display: 'none' },

                '& .RaLogin-card': {
                    marginTop: 0,
                    width: '100%',
                    maxWidth: 940,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: 'hidden',
                    borderRadius: 4,
                    boxShadow: `0 18px 50px ${alpha(theme.palette.common.black, 0.18)}`,
                },
            };
        }}
    >
        <BrandPanel />
        <FormPanel />
    </RaLogin>
);

export default LoginNew;
